import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { ALERT, PALETTE, axisProps, fmtMoney, fmtMoneyShort, fmtNum, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// What the shelves are worth and how long they will last. Quantities and values come from the
// batch ledger (received minus consumed per receipt line); the daily rate comes from actual
// dispensing over the chosen window, so days-of-stock is measured, not guessed.

const TIER = {
  CRITICAL: { label: 'Critical', cls: 'bg-rose-100 text-rose-700' },
  LOW: { label: 'Low', cls: 'bg-amber-100 text-amber-700' },
  OK: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700' },
  NO_DATA: { label: 'No usage data', cls: 'bg-gray-100 text-gray-600' },
};

const COLUMNS = [
  { key: 'name', label: 'Medicine' },
  { key: 'genericName', label: 'Generic' },
  { key: 'qty', label: 'Qty', align: 'right' },
  { key: 'purchaseValue', label: 'Value @ Purchase', align: 'right', render: (r) => fmtMoney(r.purchaseValue) },
  { key: 'sellingValue', label: 'Value @ Selling', align: 'right', render: (r) => fmtMoney(r.sellingValue) },
  { key: 'dailyRate', label: 'Daily Use', align: 'right', render: (r) => r.dailyRate ?? '—' },
  { key: 'daysLeft', label: 'Days Left', align: 'right', render: (r) => r.daysLeft ?? '—' },
  {
    key: 'tier', label: 'Health',
    render: (r) => {
      const t = TIER[r.tier] || TIER.NO_DATA;
      return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${t.cls}`}>{t.label}</span>;
    },
    csv: (r) => (TIER[r.tier] || TIER.NO_DATA).label,
  },
];

const StockValuationReport = () => {
  const drill = useDrill();
  const [window, setWindow] = useState(30);
  const fetcher = useCallback(() => reportsAPI.getStockValuation(window), [window]);
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Stock @ Purchase', value: fmtMoney(k.purchaseValue ?? 0) },
    { label: 'Stock @ Selling', value: fmtMoney(k.sellingValue ?? 0) },
    { label: 'Potential Margin', value: fmtMoney(k.potentialMargin ?? 0) },
    { label: 'Medicines in Stock', value: fmtNum(k.medicinesInStock ?? 0) },
    { label: 'Critical (<7 days)', value: fmtNum(k.critical ?? 0), tone: Number(k.critical) > 0 ? 'alert' : undefined },
    { label: 'Out of Stock', value: fmtNum(k.outOfStock ?? 0), tone: Number(k.outOfStock) > 0 ? 'alert' : undefined },
  ];

  const top10 = useMemo(
    () => (data?.rows || []).slice(0, 10).map((r) => ({ name: r.name, purchaseValue: Number(r.purchaseValue) })),
    [data]
  );

  const tiers = useMemo(() => {
    const counts = {};
    (data?.rows || []).forEach((r) => { counts[r.tier] = (counts[r.tier] || 0) + 1; });
    return Object.entries(counts).map(([tier, value]) => ({ name: TIER[tier]?.label || tier, tier, value }));
  }, [data]);

  const tierColor = { CRITICAL: ALERT, LOW: PALETTE[2], OK: PALETTE[0], NO_DATA: PALETTE[5] };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {[30, 60, 90].map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                window === w ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {w}-day usage
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Daily use is measured from dispensing since {data?.dataFloorDate || ''}
          {data?.windowClamped ? ' (window shortened: no dispensing records exist before then)' : ''}.
        </p>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartPanel title="Top 10 medicines by stock value">
          {top10.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No stock on the shelves</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, top10.length * 32)}>
              <BarChart data={top10} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
                <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
                <YAxis type="category" dataKey="name" {...axisProps} width={150} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="purchaseValue" name="Value" radius={[0, 4, 4, 0]} barSize={16}>
              {top10.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        <ChartPanel title="Stock health">
          {tiers.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No stock on the shelves</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={tiers} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                  {tiers.map((t) => (
                    <Cell key={t.tier} fill={tierColor[t.tier] || PALETTE[5]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'stockMedicine', value: r.name })}
        title="Stock by medicine"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`stock-valuation-${data?.asOf || ''}`}
        emptyText="No stock on the shelves"
        footnote={`As of ${data?.asOf || ''} · usage measured over ${data?.windowDays || window} days`}
      />

      <ReportPrintLayout
        title="Stock Valuation & Health"
        subtitle={`As of ${data?.asOf || ''} · usage window ${data?.windowDays || window} days`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Stock by medicine', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default StockValuationReport;
